import {UserInterfaceActions} from '../../Components/Context/UserInterface/UserInterfaceActionTypes';
import {UserContextState} from '../../Components/Context/UserInterface/UserInterfaceContext';
import {DialogStatus} from '../../Enums';
import {ProductLayout} from '@taopix/taopix-core/dist/types/types';
import {PageConstants, ViewMode} from '@taopix/taopix-core';

/**
 * Checks if the number of pages is valid for the page insert count.
 *
 * @param pageCount The number of pages.
 * @param pageInsertCount The page insert count
 * @returns True if the page count is valid.
 */
export const hasValidPageCount = (pageCount: number, pageInsertCount: number): boolean => {
  return 0 === pageCount % pageInsertCount;
};

/**
 * Function to call when the page count has changed.
 *
 * @param payload Object with data to check.
 * @param ui The UI context.
 * @param productLayout The product layout.
 * @param pageCount The total page count, including page quantity
 */
export const onPageCountChanged = (
  payload: {spineResized: boolean},
  ui: UserContextState,
  productLayout: ProductLayout,
  pageCount: number
): void => {
  // Page count is valid for the page insert count value.
  if (
    !hasValidPageCount(pageCount, productLayout.pageinsertcount) &&
    DialogStatus.Seen !== ui.state.dialogs.pageCountChangeNotification
  ) {
    ui.dispatch(UserInterfaceActions.setFlags({pageCountChanged: true}));
  }

  // If the spine resized.
  if (
    payload?.spineResized &&
    1 === productLayout.cover.cover1active &&
    1 === productLayout.cover.cover1flexiblespine
  ) {
    // Show that the cover needs checking each time.
    ui.dispatch(UserInterfaceActions.setFlags({coverNeedsChecking: true}));
    ui.dispatch(
      UserInterfaceActions.setDialogStatus({coverSpineChangeNotification: DialogStatus.Closed})
    );
  }
};

/**
 * Gets the correct next page number for add/delete pages based on if
 * the project pages spread or not.
 *
 * If the provided page number is the front cover, then the
 * next page number would be 0.
 *
 * @param pageNumber The page number to get the next number from.
 * @param productLayout The product layout object.
 * @returns The corrected page number.
 */
export const getAddOrDeletePageNumber = (
  pageNumber: number,
  productLayout: ProductLayout
): number => {
  if (PageConstants.FrontCoverPageNumber === pageNumber) {
    return 0;
  }

  return pageNumber + (1 === productLayout.spreads ? 2 : 1);
};
